/**
 * UI permission-keys for the `TIME_TRACKING` route.
 *
 * ## ⚠️ PROVISIONAL — drafted by FE, not yet reconciled with acl-svc
 * Split ownership (PRD `time-tracking-module.md` OQ-7): **FE drafts** this catalogue, **`be`** owns
 * the authoritative keys (it is building the BE ACL surface and D-5's per-worker scoping), and the
 * two reconcile through `/sync-ui-permissions` once BE's side lands. Treat every string here as a
 * proposal until that sync runs.
 *
 * ## Where these came from
 * Harvested from the source app's 42 `hasPermission(...)` call sites (PRD §3.1) and collapsed to
 * ~30 by dropping the deferred surfaces (import/export, reporting, enrolment requests,
 * integrations, team overview). Two source bugs are deliberately NOT reproduced:
 *   - the `timeOffListing::` / `timeOffsListing::` double prefix, and
 *   - `sidebar::timeLogsTeamOverviewRoute`, which was commented out at its own route.
 *
 * ## These gate CAPABILITY, not ROW VISIBILITY — and that distinction is load-bearing
 * D-5: v1 keeps per-worker row scoping, fail-closed, ON TOP of tenant isolation. A key says
 * *"you may approve"*; the server-side visibility grant says *"whose"*. Both must hold.
 * **Holding a key is not evidence that rows will be returned** — so a screen must never infer
 * "there is no data" from "I have the key and got an empty list". See PRD §2.0.
 */
export const TimeTrackingAclKeys = {
  /** Access to the `/time` subtree at all — the sidebar entry and every child route. */
  moduleRead: 'time:module:read',

  // ── Time logs ──────────────────────────────────────────────────────────────
  logsRead: 'time:logs:read',
  logsWrite: 'time:logs:write',
  logsSubmit: 'time:logs:submit',
  logsApprove: 'time:logs:approve',
  logsReject: 'time:logs:reject',
  logsDelete: 'time:logs:delete',
  logsRecall: 'time:logs:recall',
  /**
   * Withdraw an APPROVED log with a required reason — distinct from delete (T0058). The BE's own
   * transition table (`ALLOWED_TRANSITIONS`, time-tracking-svc) allows CANCELLED from every
   * non-terminal status, but the reference product only ever offers this action once a log is
   * APPROVED — before that, Delete already covers "get rid of this", and offering both on the same
   * DRAFT/SUBMITTED row would be two competing affordances for one outcome. Followed that judgment
   * rather than the BE's full permissiveness.
   */
  logsCancel: 'time:logs:cancel',
  /**
   * Provisional — drafted by `fe` ahead of BE seeding, same lifecycle as `logsCancel` (T0058):
   * fails closed (invisible) until `be` seeds it. Gates the Create/Edit panel's `Assign To` field's
   * RENDER condition — a manager logging one entry on another worker's behalf (T06,
   * time-logs-closeout) — distinct from `logsWrite` (writing your own log). Matches, but does not
   * duplicate, the field's own write-grant precondition (`time:write` for AGENCY/CLIENT/
   * PLATFORM_ADMIN, confirmed via direct DB query as NOT yet seeded — only `worker-admin` holds
   * it): this key gates whether the field APPEARS, the write-grant separately gates whether it can
   * actually submit. Reconcile once `be` confirms the grant is live.
   */
  logsWriteOther: 'time:logs:write_other',
  timerUse: 'time:timer:use',

  // ── Timesheets ─────────────────────────────────────────────────────────────
  /** My own timesheets. Every worker holds this. */
  timesheetsRead: 'time:timesheets:read',
  /**
   * EVERYONE's timesheets — the cross-worker listing (§2.5).
   *
   * Deliberately NOT `timesheetsRead`. Reading your own timesheets and reading the whole tenant's
   * are different capabilities, and collapsing them put "All timesheets" in front of a worker
   * during T07 — caught by a test asserting the worker's exact tab set. This is the capability half
   * of D-5; the server-side visibility grant is the scope half, and both must hold.
   */
  timesheetsReadAll: 'time:timesheets:read_all',
  timesheetsApprove: 'time:timesheets:approve',
  timesheetsReject: 'time:timesheets:reject',
  timesheetsResync: 'time:timesheets:resync',

  // ── Work-time configuration ────────────────────────────────────────────────
  settingsRead: 'time:settings:read',
  settingsWrite: 'time:settings:write',

  // ── Time off ───────────────────────────────────────────────────────────────
  offRead: 'time:off:read',
  offRequest: 'time:off:request',
  offApprove: 'time:off:approve',
  offReject: 'time:off:reject',
  offCancel: 'time:off:cancel',
  offBulkCreate: 'time:off:bulk_create',

  // ── Leave policies ─────────────────────────────────────────────────────────
  policiesRead: 'time:policies:read',
  policiesWrite: 'time:policies:write',
  /** Apply a structural policy change immediately, bypassing versioning. */
  policiesForceApply: 'time:policies:force_apply',
  policiesEnrol: 'time:policies:enrol',
  /** Edit a person's accrued balance directly — a person fact, keyed on canonicalPersonId. */
  balancesWrite: 'time:balances:write',

  // ── Holiday calendars ──────────────────────────────────────────────────────
  holidaysRead: 'time:holidays:read',
  holidaysWrite: 'time:holidays:write',
  /** A worker consuming their own optional-holiday entitlement. */
  holidaysAvail: 'time:holidays:avail',
  holidaysSetLimit: 'time:holidays:set_limit',
  holidaysAssociate: 'time:holidays:associate',

  // ── Leave-policy / holiday-calendar hierarchy (LPH, ADR-028) ───────────────
  // SEEDED, unlike the provisional keys above: acl-svc `apps/acl-svc/config/ui-permissions.csv:416-425` (LPH-T10), and
  // returned by `myPermissions(route: TIME_TRACKING)` on dev (2026-10-24). VENDOR = CLIENT on every row; `*:write_global` is
  // PLATFORM_ADMIN only; `leave-enrolments:request` is WORKER only. Read through `useHierarchyAccess`, never ad hoc.
  leavePoliciesRead: 'time:leave-policies:read',
  /** `time:configure` — own-tenant policy writes (create, version, cancel, delete, fork, tiers). */
  leavePoliciesWrite: 'time:leave-policies:write',
  /** `time:platform-configure` — GLOBAL (platform baseline) policy writes. */
  leavePoliciesWriteGlobal: 'time:leave-policies:write_global',
  holidayCalendarsRead: 'time:holiday-calendars:read',
  holidayCalendarsWrite: 'time:holiday-calendars:write',
  holidayCalendarsWriteGlobal: 'time:holiday-calendars:write_global',
  leaveEnrolmentsRead: 'time:leave-enrolments:read',
  /** `time:write` — a worker requests or withdraws their own enrolment. */
  leaveEnrolmentsRequest: 'time:leave-enrolments:request',
  /** `time:approve` — approve a request, or enrol another worker directly. */
  leaveEnrolmentsReview: 'time:leave-enrolments:review',
  /** `time:reject`. */
  leaveEnrolmentsReject: 'time:leave-enrolments:reject',
} as const;

export type TimeTrackingAclKey = (typeof TimeTrackingAclKeys)[keyof typeof TimeTrackingAclKeys];
